/**
 * Base Config Service
 * ============================
 *
 * We will use differents configServices
 * - web-components : some configs used in this lib
 * - platform : dsame interface but to focus on vars used in this lib
 * - shared : idem but for everithing shared between apps
 * - ? app related (ex: admin, organization, user-account...) : every apps can implement this for it's own config
 *
 * All our configServices will extends this same base class
 * - inject default config (typed)
 * - accept some extra config (not typed)
 *
 * So every instances of configService can ensure some config to pass to this extended class
 * - WebComponent simply inject default vars
 * - PlatformConfigService will inject default vars + env vars + auth config
 */
import type { Signal, WritableSignal } from '@angular/core';
import { computed, Inject, Injectable, Optional, signal } from '@angular/core';

import { deepMerge } from '../../utils/operators';

@Injectable({
  providedIn: 'root',
})
export class ConfigService<T extends Record<string, unknown>> {
  protected _config: WritableSignal<T>;

  constructor(
    // eslint-disable-next-line @angular-eslint/prefer-inject
    @Optional() @Inject('DEFAULT_CONFIG') defaultConfig: Partial<T>,
    /** any extra config - recommanded to pass merge of .env, auth etc in one object */
    // eslint-disable-next-line @angular-eslint/prefer-inject
    @Optional() @Inject('EXTRA_CONFIG') extraConfig: Record<string, unknown>,
  ) {
    this._config = signal(deepMerge<T>(defaultConfig, extraConfig ?? {}));
  }

  /** get the whole config object (signal) */
  public get config(): Signal<T> {
    return this._config.asReadonly();
  }

  /** Read memory first, then persistent storage, then the supplied default. */
  public get<K extends keyof T>(key: K, defaultValue?: T[K]): T[K] {
    const value = this.config()[key];
    if (value !== undefined && value !== null) return value;

    const stored = this.readStoredValue(String(key)) as T[K] | undefined;
    return stored ?? (defaultValue as T[K]);
  }

  /** get a config option
   * ```
   * `getComputed('app')`               //  Signal<AppIdentity>
   * `getComputed<AppIdentity>('app')`  //  Signal<AppIdentity>
   * ```
   */
  public getComputed<K extends keyof T>(key: K): Signal<T[K]>;
  /** get a config option - with default of same type
   * ```
   * `getComputed('topTabs', [])`                 // Signal<never[]>
   * `getComputed<MenuItemType[]>('topTabs', [])` // Signal<MenuItemType[]>
   * `getComputed<string[]>('topTabs', [])`       // Signal<string[]>
   * ```
   */
  public getComputed<R>(key: string, defaultValue?: R): Signal<R>;
  /** get a config option - with default of any type
   * ```
   * `getComputed<MenuItemType[]>('topTabs', [])`      // Signal<MenuItemType[]>
   * `getComputed<'topTabs', string[]>('topTabs', [])` // Signal<string[] | MenuItemType[]>
   * ```
   */
  public getComputed<K extends keyof T, D extends unknown | undefined | null>(
    key: K,
    defaultValue?: D,
  ): Signal<Exclude<T[K], undefined> | D>;
  /** get a config option (with updates : signal) */
  public getComputed<K extends keyof T | string, D = undefined>(
    key: K,
    defaultValue?: D,
  ): Signal<unknown> {
    return computed(() => {
      const config = this.config();
      if (config[key] === undefined) return defaultValue as T[K];

      return this.config()[key];
    });
  }

  /** set a config option
   * simply set new value - may erase existant data
   * Persistent values share the localStorage object "evc-config".
   */
  public set<K extends keyof T>(key: K, value: Partial<T[K]>, persistent = false): void {
    this._config.update((config) => ({
      ...config,
      [key]: value,
    }));
    if (persistent) {
      try {
        const stored = this.readStoredConfig();
        localStorage.setItem('evc-config', JSON.stringify({ ...stored, [key]: value }));
      } catch (error) {
        console.warn(`Could not persist config "${String(key)}" to localStorage`, error);
      }
    }
  }

  /** merge a config option
   * use deepMerge so ensure safe update - may keep existant data + add new ones
   */
  public merge<K extends keyof T>(key: K, value: Partial<T[K]>): void {
    this._config.update((config) => deepMerge(config, { [key]: value }));
  }

  /** provide a whole config object (merge with config) */
  public provideConfig(payload: Partial<T>): void {
    this._config.update((config) => deepMerge(config, payload));
  }

  /** Read one stored option in local storage. */
  private readStoredValue(key: string): unknown {
    try {
      const stored = this.readStoredConfig();
      return Object.hasOwn(stored, key) ? stored[key] : undefined;
    } catch (error) {
      console.warn(`Could not restore config "${key}" from localStorage`, error);
      return undefined;
    }
  }

  /** Read the shared storage object. */
  private readStoredConfig(): Record<string, unknown> {
    const stored = localStorage.getItem('evc-config');
    if (stored === null) return {};

    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      throw new Error('Stored "evc-config" must be a JSON object');
    }

    return parsed as Record<string, unknown>;
  }
}
